import api from './api';
import { InventoryItem, StockLevel, Warehouse, ApiResponse, PaginatedResponse } from '../types';

export interface CreateInventoryRequest {
  sku: string;
  name: string;
  description?: string;
  category: string;
  unitOfMeasure: string;
  reorderLevel: number;
  reorderQuantity: number;
  costPerUnit: number;
  sellingPrice: number;
  supplier: string;
  location: string;
  currentStock: number;
  isActive: boolean;
}

export interface UpdateInventoryRequest extends Partial<CreateInventoryRequest> {}

// New types for ICS inventory system
export interface Product {
  sku: string;
  name: string;
  chinese_name?: string;
  item_type: string;
  category: string;
  packing?: string;
  unit_weight?: number;
  unit_of_measure: string;
  selling_price?: number;
  cost_per_unit: number;
  reorder_point: number;
  disabled?: string;
  is_raw_material?: string;
  is_finish_product?: string;
  location_code?: string;
  current_stock: number;
  created_at: string;
  updated_at: string;
}

export interface CapacityInfo {
  location_code: string;
  location_name: string;
  total_products: number;
  total_quantity: number;
  total_weight: number;
  total_value: number;
  low_stock_items: number;
}

export interface ProductionAssignment {
  session_number: string;
  product_sku: string;
  product_name: string;
  raw_material_lot: string;
  raw_material_item_number: string;
  initial_material_weight: number;
  target_output_weight: number;
  actual_output_weight: number;
  waste_weight: number;
  yield_percentage: number;
  status: string;
  station_id: number;
  station_name: string;
  assigned_operator: string;
  started_at: string;
  completed_at?: string;
  location_code: string;
  location_name: string;
  warehouse_code: string;
}

export interface MaterialConsumption {
  job_number: string;
  product_sku: string;
  product_name: string;
  output_quantity: number;
  production_date: string;
  material_sku: string;
  material_name: string;
  input_quantity: number;
  material_unit: string;
  material_cost: number;
  consumption_rate: number;
}

export interface WasteStats {
  total_waste_weight: number;
  completed_sessions: number;
  average_yield_percentage: number;
}

// Lot Inventory types
export interface Lot {
  id: string;
  item_number: string;
  transfer_number: string;
  item_name: string;
  chinese_name?: string;
  actual_quantity: number;
  actual_weight: number;
  current_amount: number;
  status: string;
  is_ni_remnant?: boolean | null;
  approval_status?: string;
  verification_status?: string;
  verified_by?: string;
  verified_by_name?: string;
  verified_at?: string;
  verification_notes?: string;
  expected_quantity?: number;
  expected_weight?: number;
  expiry_date?: string;
  received_date: string;
  received_by?: string;
  received_by_name?: string;
  location_code?: string;
  location_name?: string;
  created_at: string;
  updated_at: string;
  allocated_session?: string;
  allocated_weight?: number;
  is_packing_material?: string;
  item_sub_quantity?: number;
  unit_weight?: number;
  // Freshness fields (only present for by_product_p lots)
  freshness_age_days?: number | null;
  freshness_badge_key?: 'fresh' | 'use_soon' | 'old_stock' | null;
  freshness_badge_label?: string | null;
  freshness_badge_color?: 'green' | 'orange' | 'red' | null;
}

export interface LotsResponse {
  lots: Lot[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

// Pending approval lot (on-hold items awaiting manager review)
export interface PendingApprovalLot {
  id: string;
  item_number: string;
  transfer_number: string;
  actual_quantity: number;
  actual_weight: number;
  status: string;
  on_hold_reason: string;
  verification_notes?: string;
  verified_by: string;
  verified_by_name?: string;
  verified_at: string;
  received_date: string;
  approval_status: string;
  item_english_name?: string;
  item_chinese_name?: string;
}

// Approval history record (for audit trail report)
export interface ApprovalHistoryRecord {
  id: string;
  item_number: string;
  transfer_number: string;
  actual_quantity: number;
  actual_weight: number;
  on_hold_reason: string;
  approval_status: 'approved' | 'rejected';
  approved_by: string;
  approval_date: string;
  approval_notes: string | null;
  received_date: string;
  verified_by: string | null;
  verified_at: string | null;
  verified_by_name: string | null;
  item_english_name: string | null;
  item_chinese_name: string | null;
  manager_name: string;
}

export interface LotsSummary {
  total_lots: number;
  available_lots: number;
  reserved_lots: number;
  depleted_lots: number;
  rejected_lots: number;
  total_available_weight: number;
  expired_lots: number;
  expiring_soon_lots: number;
}

export interface ByproductSummaryItem {
  item_number: string;
  item_name: string;
  total_weight: number;
  case_count: number;
  created_at: string | null;
}

export interface PackingLotsSummary {
  total_packing_items: number;
  total_packing_lots: number;
  available_packing_lots: number;
  low_stock_items: number;
}

export interface PackingLinkedProduct {
  product_item_number: string;
  product_name: string;
  product_chinese_name?: string;
  packing_level?: string;
}

// Inventory Transaction types (ledger)
export interface InventoryTransaction {
  id: string;
  item_number: string;
  item_name: string;
  transaction_type: string;
  quantity_change: number;
  balance_before: number;
  balance_after: number;
  reference_type?: string;
  reference_id?: string;
  session_number?: string;
  notes?: string;
  created_at: string;
  created_by?: string;
}

export interface InventoryTransactionsResponse {
  transactions: InventoryTransaction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

// Finished Products (Boxes) types
export interface FinishedProduct {
  id: string;
  box_number: string;
  item_number: string;
  product_name: string;
  chinese_name?: string;
  is_byproduct_marketable?: boolean;
  actual_weight: number;
  status: string;
  lot_number?: string;
  material_type?: string;
  location_code?: string;
  location_name?: string;
  barcode_number?: string;
  packed_at: string;
  packed_by?: string;
  session_number?: string;
  shipped_at?: string;
  voided_at?: string;
  void_reason?: string;
  created_at: string;
}

export interface FinishedProductsResponse {
  boxes: FinishedProduct[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface FinishedProductsSummary {
  total_boxes: number;
  packed_boxes: number;
  shipped_boxes: number;
  on_hand_boxes: number;
  voided_boxes: number;
  total_weight: number;
  packed_weight: number;
  on_hand_weight: number;
  shipped_weight: number;
  fp_ni_on_hand_weight?: number;
  fp_ni_shipped_weight?: number;
  unique_products: number;
  boxes_today: number;
  sessions_completed?: number;
}

export interface GroupedFinishedProduct {
  item_number: string;
  product_name: string;
  chinese_name?: string;
  is_byproduct_marketable?: boolean;
  /** On-hand case count (packed − DTO shipped boxes); may be negative when shipment exceeds packed. */
  box_count: number;
  on_hand_count: number;
  packed_count: number;
  shipped_count: number;
  shipped_fpni_quantity?: number;
  fp_ni_lot_count?: number;
  fp_ni_on_hand_weight?: number;
  fp_ni_shipped_weight?: number;
  has_fp_ni?: boolean;
  is_fp_ni_only?: boolean;
  packed_weight?: number;
  shipped_weight?: number;
  shipped_box_weight?: number;
  on_hand_box_weight?: number;
  on_hand_weight: number;
  total_weight: number;
  last_packed_at: string | null;
  expected_count: number;
}

// Warehouse Transfer types (for raw material receiving)
export interface WarehouseTransferDetail {
  id: number;
  transfer_number: string;
  item_number: string;
  item_name?: string;
  chinese_name?: string;
  english_brand_name?: string;
  chinese_brand_name?: string;
  item_unit?: string;
  item_sub_unit?: string;
  use_sub_unit?: string;
  // WCO-2632: backend-resolved UOM — item_sub_unit for sub-unit lines (use_sub_unit='Y'), else item_unit.
  resolved_unit?: string;
  packing?: string;
  quantity_request?: number;
  quantity_shipped?: number;
  weight_shipped?: number;
  date_code?: string;
  is_frozen?: string;
  receiving_scan_complete?: string;
  is_raw_material?: string;
  unit_weight?: number;
  upc?: string;
  already_received?: boolean;
  received_quantity?: number;
  received_weight?: number;
  weights?: DetailWeight[];
  // Additional fields for extended detail view
  remark?: string;
  special_request?: string;
  avg_sold?: number;
  avg_back?: number;
  qs_type?: string;
  duration?: string;
  scan_complete?: string;
  qs_pend?: string;
  qs_rec_number?: string;
  salesperson_qs1?: string;
  q_item?: string;
}

export interface WarehouseTransfer {
  transfer_number: string;
  transfer_date?: string;
  request_date?: string;
  warehouse_code_from?: string;
  location_code_from?: string;
  warehouse_code_to?: string;
  location_code_to?: string;
  is_complete?: string;
  memo?: string;
  receive_by?: string;
  operator_id?: string;
  request_by?: string;
  m_date?: string;
  m_time?: string;
  details: WarehouseTransferDetail[];
}

export interface BoxWeight {
  id: number;
  weight: number;
  item_number?: string;
  date_code?: string;
}

// ICS Transfer Search types
export interface IcsTransferSearchParams {
  search_type: 'warehouse_code' | 'transfer_number' | 'request_date' | 'transfer_date' | 'pending' | 'hte';
  value?: string;
  value_to?: string;
  location_from?: string;
  location_to?: string;
  item_code?: string;
  transfer_number?: string;
  show_vb6?: boolean;
  include_drafts?: boolean;
  status?: string;
  source?: string;
  source_exclude?: string; // WCO-2536: exclude a source (IN tab = everything but daily-transfer-out)
  page?: number;
  limit?: number;
}

export interface IcsTransferCounts {
  all: number;
  pending: number;
  complete: number;
  cancelled: number;
  draft: number;
}

export interface IcsTransferSummaryRow {
  transfer_number: string;
  transfer_date: string;
  request_date: string;
  warehouse_code_from: string;
  location_code_from: string;
  warehouse_code_to: string;
  location_code_to: string;
  is_complete: string;
  memo: string;
  operator_id: string;
  request_by: string;
  m_date: string;
  m_time: string;
  from_description: string;
  to_description: string;
  item_count: number;
  hte_status?: string;    // null for legacy ICS, 'draft'|'pending'|'scanned'|'complete'|'cancelled' for HTE
  hte_source?: string;    // null for legacy, 'manual'|'auto-generated'|'daily-transfer-out' for HTE
  hte_transfer_type?: string;
}

export interface IcsTransferSearchResponse {
  transfers: IcsTransferSummaryRow[];
  total: number;
  page: number;
  limit: number;
  counts?: IcsTransferCounts;
}

export interface DetailWeight {
  weight_id: number;
  weight: number;
}

export interface ReceiveLotRequest {
  transfer_number: string;
  item_number: string;
  quantity: number;
  weight: number;
  expiry_date?: string;
  location_code?: string;
  received_by?: string;
}

export interface ScannedBox {
  sequence: number;
  item_number: string;
  item_name?: string;
  weight: number;
  scanned_at: string;
}

// Transfer Verification types
export interface TransferItem {
  item_number: string;
  item_name: string;
  chinese_name?: string;
  expected_quantity: number;
  expected_weight: number;
  already_received: boolean;
  is_packing_material?: string;
  item_sub_quantity?: number;
  unit_weight?: number;
  packing?: string;
  item_unit?: string;
}

export interface UnprocessedTransfer {
  transfer_number: string;
  transfer_date: string;
  location_code_from: string;
  location_code_to: string;
  warehouse_code_from: string;
  warehouse_code_to: string;
  is_complete: string;
  items: TransferItem[];
  total_items: number;
  received_items: number;
}

export interface ScanDetail {
  scan_order: number;
  weight: number;
  raw_weight?: number;
  gtin: string | null;
  status: 'accepted' | 'rejected' | 'on_hold';
  remark?: string;
  weight_ai?: string | null;
  weight_unit?: 'kg' | 'lb' | null;
}

export interface ReceiveItemDecision {
  item_number: string;
  action: 'approve' | 'reject' | 'hold';
  expected_quantity: number;
  expected_weight: number;
  actual_quantity: number;
  actual_weight: number;
  notes?: string;
  on_hold_reason?: string;
  scan_details?: ScanDetail[];
}

export interface ReceiveAndVerifyRequest {
  transfer_number: string;
  verified_by: string;
  items: ReceiveItemDecision[];
}

export interface LotVerificationHistoryEntry {
  id: number;
  lot_id: string;
  action: string;
  old_value: string;
  new_value: string;
  changed_by: string;
  notes?: string;
  created_at: string;
}

// Verification History types
export interface LotScanDetail {
  lot_id: string;
  scan_order: number;
  item_number: string;
  weight: number;
  gtin: string;
  status: string;
  remark?: string;
  created_at?: string;
}

export interface VerifiedTransferItem {
  lot_id?: string;
  item_number: string;
  item_name?: string;
  chinese_name?: string;
  actual_quantity: number;
  actual_weight: number;
  expected_quantity?: number;
  expected_weight?: number;
  verification_status: 'approved' | 'verified' | 'rejected' | 'on_hold';
  verification_notes?: string;
  scan_details?: LotScanDetail[];
}

export interface VerifiedTransfer {
  transfer_number: string;
  verified_at: string;
  verified_by: string;
  verified_by_name?: string;
  item_count: number;
  total_quantity: number;
  total_weight: number;
  approved_count: number;
  on_hold_count: number;
  rejected_count: number;
  items: VerifiedTransferItem[];
}

export interface VerificationHistoryResponse {
  transfers: VerifiedTransfer[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface Station {
  station_id: number;
  station_name: string;
  warehouse_code: string;
  location_code: string;
  location_name: string;
  status: 'idle' | 'inactive' | 'paused' | 'active';
  station_type: string;
  assigned_product_sku?: string | null;
  assigned_product_name?: string | null;
  session_status?: string | null;
  enabled?: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductionLocation {
  location_code: string;
  location_name: string;
  warehouse_code: string;
  created_at: string;
  updated_at: string;
}

export interface CreateStationRequest {
  station_name: string;
  warehouse_code: string;
  location_code: string;
  station_type?: string;
  assigned_product_sku?: string;
}

export interface UpdateStationRequest extends Partial<CreateStationRequest> {}

export interface ProductsResponse {
  products: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export type { InventoryItem };

// Box Search types
export interface BoxSearchParams {
  item_number?: string;
  date_code?: string;
  packed_from?: string;
  packed_to?: string;
  location_code?: string;
  include_voided?: boolean;
  barcode?: string;
  box_number?: string;
  page?: number;
  limit?: number;
}

export interface BoxSearchResult {
  box_id: string;
  box_number: string;
  barcode_number: string;
  item_number: string;
  product_name: string;
  chinese_name: string;
  actual_weight: number;
  lot_number: string;
  packed_at: string;
  operator_employee_number: string | null;
  operator_name: string | null;
  packer_employee_number: string | null;
  packer_name: string | null;
  status: string;
  location_code: string;
  session_number: string;
  station_name: string;
  raw_material_item_number: string | null;
}

// Marketable buffer/case row returned when a scanned barcode resolves to a by-product label
export interface ByproductBarcodeResult {
  id: string;
  byproduct_item_number: string;
  source_material_item_number: string | null;
  status: string;
  barcode_number: string | null;
  item_name: string | null;
  chinese_name: string | null;
  // buffer-only
  reserved_weight?: number;
  last_session_number?: string;
  last_sub_session_number?: string;
  last_production_date?: string;
  // case-only
  case_weight?: number;
  session_number?: string;
  sub_session_number?: string;
  production_date?: string;
}

export interface BoxSearchResponse {
  boxes: BoxSearchResult[];
  total: number;
  page: number;
  limit: number;
  summary: {
    total_boxes: number;
    total_weight: number;
  };
  result_type?: 'box' | 'buffer' | 'case';
  buffer?: ByproductBarcodeResult | null;
  case?: ByproductBarcodeResult | null;
}

export const inventoryService = {
  // Inventory Items
  async getItems(params?: { page?: number; limit?: number; search?: string; category?: string }): Promise<PaginatedResponse<InventoryItem>> {
    const response = await api.get('/api/inventory/items', { params });
    return response.data;
  },

  async getItem(id: string): Promise<InventoryItem> {
    const response = await api.get(`/api/inventory/items/${id}`);
    return response.data.data;
  },

  async createItem(itemData: CreateInventoryRequest): Promise<InventoryItem> {
    const response = await api.post('/api/inventory/items', itemData);
    return response.data.data;
  },

  async updateItem(id: string, itemData: UpdateInventoryRequest): Promise<InventoryItem> {
    const response = await api.put(`/api/inventory/items/${id}`, itemData);
    return response.data.data;
  },

  async deleteItem(id: string): Promise<void> {
    await api.delete(`/api/inventory/items/${id}`);
  },

  // Toggle item status
  async toggleItemStatus(id: string): Promise<InventoryItem> {
    const response = await api.patch(`/api/inventory/items/${id}/toggle-status`);
    return response.data.data;
  },

  // Stock Management
  async getStockLevels(params?: { warehouseId?: string; itemId?: string }): Promise<StockLevel[]> {
    const response = await api.get('/api/inventory/stock', { params });
    return response.data.data;
  },

  async updateStockLevel(id: string, quantity: number, type: 'add' | 'subtract' | 'set'): Promise<StockLevel> {
    const response = await api.put(`/api/inventory/stock/${id}`, { quantity, type });
    return response.data.data;
  },

  // Warehouses
  async getWarehouses(): Promise<Warehouse[]> {
    const response = await api.get('/api/inventory/warehouses');
    return response.data.data;
  },

  async getWarehouse(id: string): Promise<Warehouse> {
    const response = await api.get(`/api/inventory/warehouses/${id}`);
    return response.data.data;
  },

  // Get categories
  async getCategories(): Promise<string[]> {
    const response = await api.get('/api/inventory/categories');
    return response.data.data || ['Electronics', 'Tools', 'Materials', 'Equipment', 'Components', 'Safety'];
  },

  // Get locations
  async getLocations(): Promise<string[]> {
    const response = await api.get('/api/inventory/locations');
    return response.data.data || ['Warehouse A', 'Warehouse B', 'Warehouse C'];
  },

  // Get suppliers
  async getSuppliers(): Promise<string[]> {
    const response = await api.get('/api/inventory/suppliers');
    return response.data.data || ['TechCorp Industries', 'ToolMaster Inc', 'MaterialSupply Co'];
  },

  // Get low stock items
  async getLowStockItems(): Promise<InventoryItem[]> {
    const response = await api.get('/api/inventory/low-stock');
    return response.data.data;
  },

  // Validate SKU
  async validateSKU(sku: string): Promise<{ available: boolean }> {
    const response = await api.get(`/api/inventory/validate/sku/${sku}`);
    return response.data.data;
  },

  // Analytics
  async getInventoryAnalytics(): Promise<any> {
    const response = await api.get('/api/inventory/analytics');
    return response.data.data;
  },

  // ===== New ICS Schema Methods =====
  async getProducts(params?: {
    page?: number;
    limit?: number;
    category?: string;
    location?: string;
    low_stock?: boolean;
    search?: string;
    type?: 'raw_material' | 'finish_product' | 'all';
    sortBy?: 'stock-desc' | 'stock-asc' | 'name-asc' | 'name-desc';
  }): Promise<ProductsResponse> {
    const response = await api.get('/inventory/products', { params });
    return response.data.data;
  },

  async getProduct(sku: string): Promise<Product> {
    const response = await api.get(`/inventory/products/${sku}`);
    return response.data.data;
  },

  async getCapacity(location?: string): Promise<CapacityInfo[]> {
    const response = await api.get('/inventory/capacity', {
      params: { location }
    });
    return response.data.data;
  },

  async getProductionAssignments(params?: {
    location?: string;
    status?: 'active' | 'completed';
  }): Promise<ProductionAssignment[]> {
    const response = await api.get('/inventory/production/assignments', { params });
    const data: ProductionAssignment[] = response.data.data;
    for (const a of data) {
      if (a.yield_percentage != null) a.yield_percentage = Number(a.yield_percentage) / 100;
    }
    return data;
  },

  async getMaterialConsumption(params?: {
    start_date?: string;
    end_date?: string;
    product_sku?: string;
  }): Promise<MaterialConsumption[]> {
    const response = await api.get('/inventory/production/consumption', { params });
    return response.data.data;
  },

  async getWasteStats(location?: string): Promise<WasteStats> {
    const response = await api.get('/inventory/waste-stats', {
      params: { location }
    });
    const data: WasteStats = response.data.data;
    if (data.average_yield_percentage != null) data.average_yield_percentage = Number(data.average_yield_percentage) / 100;
    return data;
  },

  // ===== Station Configuration CRUD =====
  // Routes via /api/stations/config/... (served by product-service)
  async getStations(params?: {
    location?: string;
    warehouse?: string;
    status?: string;
  }): Promise<Station[]> {
    const response = await api.get('/stations/config', { params });
    return response.data.data;
  },

  async getStation(id: number): Promise<Station> {
    const response = await api.get(`/stations/config/${id}`);
    return response.data.data;
  },

  async createStation(stationData: CreateStationRequest): Promise<Station> {
    const response = await api.post('/stations/config', stationData);
    return response.data.data;
  },

  async updateStation(id: number, stationData: UpdateStationRequest): Promise<Station> {
    const response = await api.put(`/stations/config/${id}`, stationData);
    return response.data.data;
  },

  async deleteStation(id: number): Promise<void> {
    await api.delete(`/stations/config/${id}`);
  },

  async updateStationStatus(id: number, status: 'active' | 'inactive' | 'offline'): Promise<Station> {
    const response = await api.patch(`/stations/config/${id}/status`, { status });
    return response.data.data;
  },

  async getProductionLocations(): Promise<ProductionLocation[]> {
    const response = await api.get('/inventory/production-locations');
    return response.data.data;
  },

  // ===== Lot Inventory =====
  async getLots(params?: {
    page?: number;
    limit?: number;
    item_number?: string;
    status?: string;
    lifecycle?: string;
    lot_type?: string;
    exclude_fp_ni?: string;
    search?: string;
    location?: string;
    verification_status?: string;
    is_packing_material?: string;
  }): Promise<LotsResponse> {
    const response = await api.get('/inventory/lots', { params });
    const data: LotsResponse = response.data.data;
    return data;
  },

  async getLotsSummary(): Promise<LotsSummary> {
    const response = await api.get('/inventory/lots/summary');
    return response.data.data;
  },

  async getByproductSummary(): Promise<ByproductSummaryItem[]> {
    const response = await api.get('/inventory/lots/byproduct-summary');
    return response.data.data;
  },

  // Packing material lot summary stats
  async getPackingLotsSummary(): Promise<PackingLotsSummary> {
    const response = await api.get('/inventory/lots/packing-summary');
    return response.data.data;
  },

  // Get linked products for a packing material item number
  async getPackingLinkedProducts(itemNumber: string): Promise<PackingLinkedProduct[]> {
    const response = await api.get('/inventory/lots/packing-products', { params: { itemNumber } });
    return response.data.data;
  },

  // ===== Finished Products (Boxes) =====
  async getFinishedProducts(params?: {
    page?: number;
    limit?: number;
    status?: string;
    item_number?: string;
    session_number?: string;
    search?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<FinishedProductsResponse> {
    const response = await api.get('/inventory/finished-products', { params });
    return response.data.data;
  },

  async getFinishedProductsSummary(): Promise<FinishedProductsSummary> {
    const response = await api.get('/inventory/finished-products/summary');
    return response.data.data;
  },

  async getFinishedProductsGrouped(params?: {
    status?: string;
    search?: string;
  }): Promise<GroupedFinishedProduct[]> {
    const response = await api.get('/inventory/finished-products/grouped', { params });
    return response.data.data;
  },

  // ===== Warehouse Transfer (Raw Material Receiving) =====

  // Get ICS warehouse codes for dropdown
  async getWarehouseCodes(): Promise<{ warehouse_code: string; description: string }[]> {
    const response = await api.get('/inventory/warehouse-codes');
    return response.data.data;
  },

  // Get ICS location codes for dropdown
  async getLocationCodes(): Promise<{ location_code: string; description: string }[]> {
    const response = await api.get('/inventory/location-codes');
    return response.data.data;
  },

  // Search ICS warehouse transfers
  async searchIcsTransfers(params: IcsTransferSearchParams): Promise<IcsTransferSearchResponse> {
    const response = await api.get('/inventory/warehouse-transfers/search', { params });
    return response.data.data;
  },

  // Lookup warehouse transfer by transfer number
  async getWarehouseTransfer(transferNumber: string): Promise<WarehouseTransfer> {
    const response = await api.get(`/inventory/warehouse-transfers/${encodeURIComponent(transferNumber)}`);
    return response.data.data;
  },

  // Get box weights for a specific item in a transfer
  async getTransferItemWeights(transferNumber: string, itemNumber: string): Promise<BoxWeight[]> {
    const response = await api.get(
      `/inventory/warehouse-transfers/${encodeURIComponent(transferNumber)}/items/${encodeURIComponent(itemNumber)}/weights`
    );
    return response.data.data;
  },

  // Download ICS transfer order as PDF
  async downloadIcsTransferPdf(transferNumber: string): Promise<Blob> {
    const response = await api.get(
      `/inventory/warehouse-transfers/${encodeURIComponent(transferNumber)}/pdf`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  // Receive raw material lot (create/update entry in ics.hte_lot)
  async receiveLot(data: ReceiveLotRequest): Promise<Lot> {
    const response = await api.post('/inventory/lots/receive', data);
    return response.data.data;
  },

  // ===== Transfer Verification =====

  // Get unprocessed ICS transfers (completed but not yet received/verified)
  // weekOffset: 0 = this week, 1 = last week, 2 = 2 weeks ago, etc.
  async getUnprocessedTransfers(weekOffset: number = 0, dateFrom?: string, dateTo?: string): Promise<UnprocessedTransfer[]> {
    const params: Record<string, any> = dateFrom && dateTo ? { dateFrom, dateTo } : { weekOffset };
    const response = await api.get('/inventory/lots/unprocessed-transfers', { params });
    return response.data.data;
  },

  // Get a single transfer by transfer number
  async getTransferByNumber(transferNumber: string): Promise<UnprocessedTransfer | null> {
    try {
      const response = await api.get(`/inventory/lots/transfer/${transferNumber}`);
      return response.data.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  // Receive and verify a transfer in one step (creates lots with verification)
  async receiveAndVerify(data: ReceiveAndVerifyRequest): Promise<Lot[]> {
    const response = await api.post('/inventory/lots/receive-and-verify', data);
    return response.data.data;
  },

  // Save receiving checkpoint (partial scan progress) for a transfer
  async saveReceivingCheckpoint(transferNumber: string, data: { supervisorId: string; supervisorName: string; step: string; scannedBoxes: any[]; decisionMode: string }): Promise<void> {
    await api.put(`/inventory/lots/transfer/${encodeURIComponent(transferNumber)}/checkpoint`, data);
  },

  // Load receiving checkpoint for a transfer (returns null if none)
  async getReceivingCheckpoint(transferNumber: string): Promise<{ supervisorId: string; supervisorName: string; step: string; scannedBoxes: any[]; decisionMode: string; savedAt: string } | null> {
    const response = await api.get(`/inventory/lots/transfer/${encodeURIComponent(transferNumber)}/checkpoint`);
    return response.data.data;
  },

  // Get verification audit trail for a lot
  async getLotVerificationHistory(lotId: string): Promise<LotVerificationHistoryEntry[]> {
    const response = await api.get(`/inventory/lots/verification-history/${lotId}`);
    return response.data.data;
  },

  // Look up item_number from GTIN/UPC barcode
  async lookupGtin(gtin: string): Promise<{ item_number: string; upc: string; item_name?: string; unit_weight?: number; brand?: string; chinese_brand?: string } | null> {
    try {
      const response = await api.get(`/inventory/gtin-lookup/${encodeURIComponent(gtin)}`);
      return response.data.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  // WCO-3121: durably record a rejected/unreadable receiving scan (raw string + parsed fields) to the
  // backend so Defect A can be diagnosed from the field — the handheld console is transient. Strictly
  // best-effort: never throws, so a diagnostic failure can't affect the scan/receive flow.
  async logScanDiagnostic(payload: {
    raw_barcode: string; reason: string; transfer_number?: string; verified_by?: string;
    parsed_weight?: number; parsed_normalized_lb?: number; weight_ai?: string; weight_unit?: string; gtin?: string;
  }): Promise<void> {
    try {
      await api.post('/inventory/lots/scan-diagnostic', payload);
    } catch {
      /* diagnostic is non-critical — swallow so it never blocks receiving */
    }
  },

  // Get per-box scan details for a specific lot
  async getLotScanDetails(lotId: string): Promise<LotScanDetail[]> {
    const response = await api.get(`/inventory/lots/${lotId}/scan-details`);
    return response.data.data;
  },

  // Get verification history of processed transfers
  async getVerificationHistory(params?: { page?: number; limit?: number }): Promise<VerificationHistoryResponse> {
    const response = await api.get('/inventory/lots/verification-history', { params });
    return response.data.data;
  },

  // ===== Mobile Supervisor Functions =====

  // Search products (for production planning)
  async searchProducts(query: string): Promise<{ items: any[] }> {
    try {
      const response = await api.get('/inventory/products', {
        params: { search: query, limit: 20 }
      });
      return { items: response.data.data?.products || [] };
    } catch (error) {
      return { items: [] };
    }
  },

  // Search materials (for production planning)
  async searchMaterials(query: string): Promise<{ items: any[] }> {
    try {
      const response = await api.get('/inventory/lots', {
        params: { search: query, status: 'available', limit: 20 }
      });
      return { items: response.data.data?.lots || [] };
    } catch (error) {
      return { items: [] };
    }
  },

  // Get inventory transaction history (ledger view)
  async getInventoryTransactions(params?: {
    page?: number;
    limit?: number;
    search?: string;
    item_number?: string;
    lot_id?: string;
    type?: string;
    date_from?: string;
    date_to?: string;
  }): Promise<InventoryTransactionsResponse> {
    const response = await api.get('/inventory/transactions', { params });
    return response.data.data;
  },

  // Get production by product for a specific date
  async getProductionByProduct(date: string): Promise<{ products: any[] }> {
    try {
      const response = await api.get('/inventory/finished-products/grouped', {
        params: { date }
      });
      return { products: response.data.data || [] };
    } catch (error) {
      return { products: [] };
    }
  },

  // Box Search - filtered list of boxes
  async searchBoxes(params: BoxSearchParams): Promise<BoxSearchResponse> {
    const response = await api.get('/inventory/boxes/search', {
      params: {
        ...params,
        include_voided: params.include_voided ? 'true' : undefined
      }
    });
    return response.data.data;
  },

  // Box Search - export to CSV
  async exportBoxSearch(params: BoxSearchParams): Promise<Blob> {
    const response = await api.get('/inventory/boxes/search/export', {
      params: {
        ...params,
        include_voided: params.include_voided ? 'true' : undefined
      },
      responseType: 'blob'
    });
    return response.data;
  },

  // Get distinct item numbers from production_inventory (for dropdown)
  async getBoxItemNumbers(): Promise<{ item_number: string; product_name: string; chinese_name?: string }[]> {
    const response = await api.get('/inventory/boxes/item-numbers');
    return response.data.data;
  },

  // ===== Manager Approval Queue =====

  // Get pending approvals (on-hold lots awaiting manager review)
  async getPendingApprovals(): Promise<PendingApprovalLot[]> {
    const response = await api.get('/inventory/lots/pending-approvals');
    return response.data.data;
  },

  // Approve an on-hold lot
  async approveLot(lotId: string, approvedBy: string, notes?: string): Promise<void> {
    await api.post(`/inventory/lots/${lotId}/approve`, { approved_by: approvedBy, notes });
  },

  // Reject an on-hold lot
  async rejectLot(lotId: string, approvedBy: string, notes: string): Promise<void> {
    await api.post(`/inventory/lots/${lotId}/reject`, { approved_by: approvedBy, notes });
  },

  // Get approval/rejection history for audit trail report
  async getApprovalHistory(params?: {
    start_date?: string;
    end_date?: string;
    approval_status?: string;
    search?: string;
  }): Promise<ApprovalHistoryRecord[]> {
    const response = await api.get('/inventory/lots/approval-history', { params });
    return response.data.data;
  },
};

export default inventoryService;
